/**
 * AIClassifyFolderDetail · AI 分类文件夹详情页（v1）
 *
 * 路由：/ai-classify/folder/:folderId
 *
 * 页面分层（自上而下）：
 *  1. DetailHeader · sticky 标题区，含 返回 / 文件夹名 / 规则提示 / meta
 *  2. SubFolderGrid · 子文件夹卡片网格（仅当 subFolderCount > 0 时渲染）
 *  3. PhotoGrid · 按日期分组的照片网格 + infinite scroll；无照片时占位
 *  4. EmptyDetailState · 当文件夹无照片且无子文件夹时显示「手动添加照片」引导
 *
 * 数据来源：
 *  · getFolderInfo · 文件夹基础信息（name / type / parentFolders）
 *  · fetchGroupFolders + fetchFolderStatistic · 子文件夹列表 + 张数 / 子级数
 *  · fetchFolderPhotos · 照片分页 + rule 字段（用来渲染顶部规则提示）
 *
 * 不持久化：本页所有数据放本地 useState，跳走即销毁；这一版还不接 redux。
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'
import {
  Button,
  Checkbox,
  Dropdown,
  Input,
  Layout,
  Menu,
  Modal,
  Spin,
  message
} from 'antd'
import {
  CaretDownOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  ExportOutlined,
  FolderOutlined,
  ScissorOutlined
} from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import * as SentryUtils from '../../../utils/sentryUtils'
import { useAppDispatch, useAppSelector, useIsAdmin } from '../../../app/hooks'
import {
  getFolderInfo,
  fetchFolderStatistic,
  fetchFolderPhotos,
  removePhotosFromFolder,
  deleteFolder,
  batchDeleteFolders,
  updateFolder
} from '../../../actions/folder'
import FoldersPicker from '../../components/AddToFolder/FoldersPicker'
import {
  deletePhoto,
  batchRemoveWatermarkCheck,
  createProducePhotoTask,
  setExportListDrawerVisible
} from '../../../actions/photo'
import { setPhotoPickerPhotos } from '../../../actions/photoPicker'
import RemoveWatermarkModal from '../../components/RemoveWatermarkModal'
// 下载命名弹窗（类型化封装）+ 下载控制器 hook · 文件夹下载与列表页共用一套
import {
  FolderDownloadModal,
  PhotoDownloadModal
} from '../components/DownloadModals'
import { useFolderDownload } from '../hooks/useFolderDownload'
import { usePhotoDownload } from '../hooks/usePhotoDownload'
// 下载记录抽屉 · 读 redux exportListDrawerVisible 渲染 Drawer；
// 下载发起后调 setExportListDrawerVisible(true) 弹起，需在本页挂载（与列表页一致）
import DownloadHistory from '../../components/DownloadHistory'
import Viewer from '../../components/ViewerV2'
import type { ViewerRef } from '../../components/ViewerV2/types'
import {
  closeSidebar,
  fetchAIFolders,
  selectSidebarOpen,
  selectSidebarWidth,
  selectFolderRefreshTick,
  setComposerFolderContext,
  setSidebarWidth,
  toggleSidebar
} from '../../../slices/aiClassify'
import { fetchFolderQuota } from '../../../slices/folderQuotaSlice'
import type { AIFoldersResult } from '../../../slices/aiClassify'
import { adaptFolderList } from '../../../slices/aiClassify/adaptFolder'
import { adaptFolderPhoto } from './utils/adaptPhoto'
import type { BackendPhoto } from './utils/adaptPhoto'
import type { FolderListItem } from '../../../types/ai-classify'
import CreateFolderModal from '../components/CreateFolderModal'
import AISidebar from '../components/AISidebar'
import ResizeHandle from '../components/AISidebar/ResizeHandle'
import { useResizable } from '../components/AISidebar/hooks/useResizable'
import AIChat from '../components/AIChat'
import AIChatHeaderActions from '../components/AIChatHeaderActions'
import { ChatActionsProvider } from '../components/AIChat/ChatActionsContext'
import { useAIClassifySession } from '../hooks/useAIClassifySession'
import { useAIClassifyLifecycle } from '../hooks/useAIClassifyLifecycle'
import DetailHeader from './DetailHeader'
import PhotoGrid, { PhotoItem } from './PhotoGrid'
import EmptyDetailState from './EmptyDetailState'
import type { ClassifyRule } from './utils/ruleText'
import { trackDetailClick } from '../utils/trackClassify'
import { useFolderCreateGate } from '../../components/FolderPaywall/useFolderCreateGate'
import styles from './Detail.module.scss'

/** AISidebar 宽度范围 · 与列表页保持一致 */
const SIDEBAR_MIN_WIDTH = 360
const SIDEBAR_MAX_WIDTH = 720

/** 构建文件夹路径字符串 · parentFolders + 当前名 → "A/B/当前" */
function buildFolderPath(
  parentFolders: Array<{ folderName: string }> | undefined,
  folderName: string
): string {
  const parts = (parentFolders || []).map((p) => p.folderName)
  parts.push(folderName)
  return parts.join('/') || '/'
}

const { Content } = Layout

/** 单页拉照片数量 · 与老 PhotosList 同款 50 */
const PHOTO_PAGE_SIZE = 50

/** 后端 getFolderInfo 返回的字段（仅声明本页用到的） */
interface BackendFolderInfo {
  folderID: number
  folderName: string
  /** 文件夹类型 · 0=普通文件夹；仅用于「新建子文件夹」门控。AI 身份另由 aiGenerated 标记 */
  folderType: number
  parentFolders?: Array<{ folderID: number; folderName: string }>
}

/**
 * 后端 fetchFolderPhotos 响应 · 仅声明本页用到的字段
 *
 * photos[] 的字段适配（smallurl→smallURL / time:string→ms 等）由
 * adaptFolderPhoto 统一处理，与待归档详情页共用（见 ./utils/adaptPhoto）。
 */
interface BackendPhotosResponse {
  photos?: BackendPhoto[]
  totalCount?: number
  rule?: ClassifyRule
}

const AIClassifyFolderDetail: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { folderId: folderIdParam } = useParams<{ folderId: string }>()
  const folderID = Number(folderIdParam)
  const groupID = useAppSelector((s) => s.group.groupId)
  const isAdmin = useIsAdmin()

  // ── 单个创建付费卡点（PRD §3.2.3）· 子文件夹也计入团队文件夹总数 ──────────
  const singleCreateGate = useFolderCreateGate()
  const refreshTick = useAppSelector(selectFolderRefreshTick)
  useEffect(() => {
    if (groupID) dispatch(fetchFolderQuota({ groupID: String(groupID) }))
  }, [groupID, refreshTick, dispatch])

  // ── AI 助手 sidebar ────────────────────────────
  const sidebarOpen = useAppSelector(selectSidebarOpen)
  const sidebarWidth = useAppSelector(selectSidebarWidth)

  // SSE 会话驱动 + 任务列表轮询 · 与列表页同款；Provider 在最外层包一份让深层组件消费
  const chatActions = useAIClassifySession()
  useAIClassifyLifecycle()

  const { width, handleMouseDown, isDragging } = useResizable({
    initialWidth: sidebarWidth,
    minWidth: SIDEBAR_MIN_WIDTH,
    maxWidth: SIDEBAR_MAX_WIDTH,
    onEnd: (w) => dispatch(setSidebarWidth(w))
  })

  /**
   * 进入详情页**不再**自动开新对话（v2 调整）
   *
   * 旧版（保留注释参考）：进入详情页若列表页已有对话 → 自动 startNewConversation
   *   动机：怕列表页 / 详情页对话上下文串台
   *
   * 新版（用户反馈 2026-05-12）：分类完成后进入详情页就被归档断流，体感差。
   *   保持同一会话，让用户能在详情页继续聊（包括对刚建好的相册追问 / 细分），
   *   不再 dispatch startNewConversation。会话上下文上下游统一，由 LLM 自己消化。
   */

  // ── 文件夹基础信息 ──────────────────────────────
  const [folderInfo, setFolderInfo] = useState<BackendFolderInfo | null>(null)
  const [folderLoading, setFolderLoading] = useState(true)

  const folderPath = useMemo(
    () =>
      buildFolderPath(folderInfo?.parentFolders, folderInfo?.folderName || ''),
    [folderInfo]
  )

  /**
   * Composer 顶部「当前文件夹」上下文写入 redux（详情页专属）。
   *
   * · folderId 进页面即有（来自路由），先写入；path 等 getFolderInfo 回来才有真实值，
   *   届时本 effect 因 folderPath 变化重跑覆盖一次（路径条在 path 就绪前自动隐藏）。
   * · 离开详情页 / 切到别的文件夹时 cleanup 清成 null，回列表页不再显示路径条，
   *   避免和列表页共用的 AIChat / Composer 串台。
   */
  useEffect(() => {
    if (!Number.isFinite(folderID) || folderID <= 0) return
    dispatch(
      setComposerFolderContext({ folderId: String(folderID), path: folderPath })
    )
    return () => {
      dispatch(setComposerFolderContext(null))
    }
  }, [folderID, folderPath, dispatch])

  // ── 子文件夹 ──────────────────────────────────
  const [subFolders, setSubFolders] = useState<FolderListItem[]>([])

  // ── 子文件夹多选 + 批量动作（仅 grid 视图）──────────
  const [subFolderSelectMode, setSubFolderSelectMode] = useState(false)
  const [selectedSubFolderIds, setSelectedSubFolderIds] = useState<Set<string>>(
    () => new Set()
  )
  /** 「移动到」picker 操作的子文件夹 id 列表 · null = 未打开 */
  const [movingFolderIds, setMovingFolderIds] = useState<string[] | null>(null)

  // ── 新建子文件夹弹窗 ──────────────────────────
  const [createSubOpen, setCreateSubOpen] = useState(false)

  // ── 照片多选 ──────────────────────────────────
  const [photoSelectMode, setPhotoSelectMode] = useState(false)
  const [selectedPhotoIds, setSelectedPhotoIds] = useState<Set<string>>(
    () => new Set()
  )
  const [removingPhotos, setRemovingPhotos] = useState(false)
  const [deletingPhotos, setDeletingPhotos] = useState(false)
  /**
   * 「移动到」picker 当前要操作的 photo id 列表 · null = 未打开
   *  · 来自批量按钮：传 [...selectedPhotoIds]
   *  · 来自右键菜单：传 [photo.id] 单条 · 或选中集合（命中右键的卡时）
   */
  const [movingPhotoIds, setMovingPhotoIds] = useState<string[] | null>(null)
  /**
   * 多选模式下"照片批量操作按钮组"的 portal 槽位 · ref 回调拿到 DOM 节点
   *
   * DetailHeader 在头部按钮区渲染一个空 span 占位（onPhotoActionsSlotRef）；
   * 本页拿到节点后把 [全选 / 已选 N / 下载 / 编辑水印 / 去水印 / 移动]
   * 一组按钮 createPortal 进去。
   */
  const [photoActionsSlot, setPhotoActionsSlot] =
    useState<HTMLSpanElement | null>(null)
  /** 去水印任务 modal 状态 · 与 photoFolders/Detail/HeaderExtra 同款字段 */
  const [removeWmModal, setRemoveWmModal] = useState<{
    visible: boolean
    taskID: string | null
    fileName: string
    photoNum: number
  }>({ visible: false, taskID: null, fileName: '', photoNum: 0 })
  /** 触发去水印任务时缓存的可去水印 srcUrls · onRetry 时复用 */
  const removeWmSrcUrlsRef = useRef<string[]>([])
  /** 当前用户 user_id · createProducePhotoTask 必传字段 */
  const userId = useAppSelector((s) => s.main.user?.user_id) as string

  // ── 照片列表 ──────────────────────────────────
  const [photos, setPhotos] = useState<PhotoItem[]>([])
  const [photoTotal, setPhotoTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [photoLoading, setPhotoLoading] = useState(false)
  const [photoEnd, setPhotoEnd] = useState(false)
  const [rule, setRule] = useState<ClassifyRule | null>(null)
  const fetchingRef = useRef(false)

  /**
   * 大图预览 ViewerV2 ref · 单图点击调 viewerRef.current.view(index) 打开
   * ViewerV2 内部依赖 Immutable.List 形态的 photos · 用 useMemo 包一下
   */
  const viewerRef = useRef<ViewerRef>(null)
  const immutablePhotos = useMemo(() => photos, [photos])

  /** 照片点击预览 · 找到 id 在 photos 中的 index 后调 viewer */
  const handlePreviewPhoto = useCallback(
    (id: string) => {
      trackDetailClick('viewOne', { path: folderPath })
      const idx = photos.findIndex((p) => p.id === id)
      if (idx >= 0 && viewerRef.current) {
        viewerRef.current.view(idx)
      }
    },
    [photos, folderPath]
  )

  /**
   * 子文件夹 fetch · 抽成 callback 让首次 mount 和"新建子文件夹后刷新"复用
   *
   * 与 FolderList 主页面同款两段串行：
   *   1. fetchGroupFolders({parentFolderID: folderID}) 拉一层
   *   2. fetchFolderStatistic 补 photoCount / folderCount 等统计
   *
   * 失败软处理：保留旧数据（不清空）+ console.error 上报
   */
  const loadSubFolders = useCallback(() => {
    if (!groupID || !Number.isFinite(folderID) || folderID <= 0) return
    Promise.resolve(
      dispatch(fetchAIFolders({ groupID, parentFolderID: folderID }))
    )
      .then((result: AIFoldersResult | undefined) => {
        // 子文件夹请求用不到 unclassifiedPhotoCount，只取 folders
        const list = result?.folders ?? []
        if (list.length === 0) {
          setSubFolders([])
          return null
        }
        const folderIDs = list.map(
          (f: { folderID: string | number }) => f.folderID
        )
        return Promise.resolve(
          dispatch(fetchFolderStatistic({ groupID, folderIDs }))
        ).then((statResp: { data?: unknown[] } | undefined) => {
          const stats = Array.isArray(statResp?.data) ? statResp!.data : []
          setSubFolders(adaptFolderList(list as never[], stats as never[]))
          return null
        })
      })
      .catch((err: unknown) => {
        // eslint-disable-next-line no-console
        console.error('详情页拉子文件夹失败', err)
      })
  }, [groupID, folderID, dispatch])

  // ── 文件夹基础 + 子文件夹 fetch（mount / folderID 变） ──
  useEffect(() => {
    if (!groupID || !Number.isFinite(folderID) || folderID <= 0) {
      navigate('/ai-classify', { replace: true })
      return
    }
    let cancelled = false

    setFolderLoading(true)
    Promise.resolve(dispatch(getFolderInfo({ groupID, folderID })))
      .then((data: unknown) => {
        if (cancelled) return
        if (!data) {
          message.error('文件夹不存在或已被删除')
          navigate('/ai-classify', { replace: true })
          return
        }
        setFolderInfo(data as BackendFolderInfo)
        const info = data as BackendFolderInfo
        trackDetailClick('enterPage', {
          path: buildFolderPath(info.parentFolders, info.folderName)
        })
      })
      .catch(() => {
        if (cancelled) return
        message.error('加载文件夹信息失败')
        navigate('/ai-classify', { replace: true })
      })
      .finally(() => {
        if (!cancelled) setFolderLoading(false)
      })

    loadSubFolders()

    return () => {
      cancelled = true
    }
  }, [groupID, folderID, dispatch, loadSubFolders, navigate])

  /**
   * AI 会话建夹任务完成后刷新子文件夹列表（bad case 修复）
   *
   * 建夹任务完成会 dispatch(bumpFolderRefreshTick) → refreshTick++（与列表页同款信号，
   * 列表页 FolderList 据此重拉顶层列表）。但上面的 mount/folderID effect 不依赖
   * refreshTick，导致详情页在 AI 建子文件夹后不刷新。这里单独监听 refreshTick 只重拉
   * 子文件夹（不重拉 folderInfo / 照片，避免整页 loading 闪烁 + 照片分页被重置）。
   *
   * 用 ref 跳过 mount 初值：mount 时 refreshTick 等于初值，交给上面的 effect 拉首屏，
   * 仅当 refreshTick 真正变化（任务完成）时才在这里重拉。
   */
  const lastRefreshTickRef = useRef(refreshTick)
  useEffect(() => {
    if (refreshTick === lastRefreshTickRef.current) return
    lastRefreshTickRef.current = refreshTick
    loadSubFolders()
  }, [refreshTick, loadSubFolders])

  // ── 照片列表 fetch（分页拼接） ──────────────────
  const loadPhotos = useCallback(
    (targetPage: number) => {
      if (fetchingRef.current) return
      if (!groupID || !Number.isFinite(folderID) || folderID <= 0) return
      fetchingRef.current = true
      setPhotoLoading(true)
      Promise.resolve(
        dispatch(
          fetchFolderPhotos({
            groupID,
            folderID,
            page: targetPage,
            pageSize: PHOTO_PAGE_SIZE
          })
        )
      )
        .then((res: BackendPhotosResponse | null | undefined) => {
          if (!res) {
            setPhotoEnd(true)
            return
          }
          // action 透传的是原始 res · photos[] 字段适配统一交给 adaptFolderPhoto
          const list = Array.isArray(res.photos) ? res.photos : []
          const mapped: PhotoItem[] = list.map(adaptFolderPhoto)
          setPhotos((prev) =>
            targetPage === 1 ? mapped : [...prev, ...mapped]
          )
          if (typeof res.totalCount === 'number') {
            setPhotoTotal(res.totalCount)
          }
          if (res.rule) setRule(res.rule)
          // 没拉到 / 拿到的少于一页 → 标记 end
          if (list.length < PHOTO_PAGE_SIZE) setPhotoEnd(true)
        })
        .catch((err: unknown) => {
          // eslint-disable-next-line no-console
          console.error('详情页拉照片失败', err)
          setPhotoEnd(true)
        })
        .finally(() => {
          fetchingRef.current = false
          setPhotoLoading(false)
        })
    },
    [groupID, folderID, dispatch]
  )

  // 切 folderID 时重置照片 + 拉第一页
  useEffect(() => {
    setPhotos([])
    setPhotoTotal(0)
    setPage(1)
    setPhotoEnd(false)
    setRule(null)
    loadPhotos(1)
    // loadPhotos 依赖 folderID/groupID/dispatch 变化时已是新引用，走这条
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderID, groupID])

  /** 滚到底部触发 · PhotoGrid 调 */
  const handleLoadMore = useCallback(() => {
    if (photoLoading || photoEnd) return
    const next = page + 1
    setPage(next)
    loadPhotos(next)
  }, [photoLoading, photoEnd, page, loadPhotos])

  // ── 导航 ───────────────────────────────────────
  const handleBack = useCallback(() => {
    navigate('/ai-classify')
  }, [navigate])

  const handleSubFolderClick = useCallback(
    (subFolderId: string) => {
      trackDetailClick('enterFolder', { path: folderPath })
      navigate(`/ai-classify/folder/${subFolderId}`)
    },
    [navigate, folderPath]
  )

  /**
   * 「新建子文件夹」 · 仅管理员可见入口。
   * 先过单个创建卡点：已达上限 → 拉开通弹窗，不开建夹弹窗；否则照常开弹窗。
   */
  const handleOpenCreateSub = useCallback(() => {
    if (!singleCreateGate()) return
    trackDetailClick('createSubFolder', { path: folderPath })
    setCreateSubOpen(true)
  }, [folderPath, singleCreateGate])

  /**
   * 子文件夹创建成功 · 不跳转，仅刷新本页的子文件夹列表
   *
   * 与顶层新建（默认行为=跳新详情）刻意不同：用户在父详情页里建子，多半还想
   * 继续往父里塞东西，跳走会打断流。bumpFolderRefreshTick 顺手发一下，让
   * 列表页（如果在背景里）也能拿到最新计数，无副作用。
   */
  const handleSubFolderCreated = useCallback(() => {
    loadSubFolders()
  }, [loadSubFolders])

  // ── 照片多选 + 批量动作 ────────────────────────

  /** 切换多选模式 · 退出时顺手清掉选中 */
  const handleTogglePhotoSelect = useCallback(() => {
    setPhotoSelectMode((prev) => {
      const next = !prev
      if (next) trackDetailClick('multiSelect', { path: folderPath })
      else trackDetailClick('cancel', { path: folderPath })
      if (!next) setSelectedPhotoIds(new Set())
      return next
    })
  }, [folderPath])

  /**
   * 切换单条勾选 · PhotoGrid 既负责整图点也负责勾选框点，都打到这一个入口
   *
   * 反勾到空集合时同步把 photoSelectMode 退回 false：否则会出现"管理态视觉
   * 残留但无选中"的中间态——头部 portal 里的批量操作按钮组还在 DetailHeader，
   * 但所有按钮都是 disabled。
   *
   * 头部 "多选" 按钮（DetailHeader）走 handleTogglePhotoSelect，是故意的
   * "先进多选态、再用 portal 内全选按钮挑"入口，不在本 handler 路径上，
   * 不会被这里误退。
   */
  const handleTogglePhotoOne = useCallback((id: string) => {
    setSelectedPhotoIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      // 与下面 setPhotoSelectMode 同回调内提交 · React 合成事件批处理为一次渲染
      setPhotoSelectMode(next.size > 0)
      return next
    })
  }, [])

  /** 全选 · 已加载到的所有照片 */
  const handleSelectAllPhotos = useCallback(() => {
    trackDetailClick('selectAll', { path: folderPath })
    setSelectedPhotoIds(new Set(photos.map((p) => p.id)))
  }, [photos, folderPath])

  /**
   * 取消全选 · portal 内"取消全选 / 全选"按钮命中已全选时走这里
   *
   * 清空选中后同步退出多选态：用户语义上 "取消全选" = "我不要批量操作了"，
   * 不退出会留头部批量按钮组残留（同 handleTogglePhotoOne 反勾到空的情况）。
   */
  const handleClearSelection = useCallback(() => {
    trackDetailClick('unSelectAll', { path: folderPath })
    setSelectedPhotoIds(new Set())
    setPhotoSelectMode(false)
  }, [folderPath])

  /**
   * 框选完成 · PhotoGrid 通过 onMarqueeSelect 上抛命中的 photoId 数组
   * 进多选 + 整体替换选中（与列表页 FolderList 同款语义；空命中已被 PhotoGrid 过滤）
   */
  const handleMarqueeSelect = useCallback((ids: string[]) => {
    setPhotoSelectMode(true)
    setSelectedPhotoIds(new Set(ids))
  }, [])

  /** 退出多选 + 清空选中 · 删除 / 移除 / 下载完成后调 */
  const exitPhotoSelect = useCallback(() => {
    setPhotoSelectMode(false)
    setSelectedPhotoIds(new Set())
  }, [])

  /**
   * 照片下载控制器 · 选中照片的命名弹窗 + 发起导出（详情页专用 hook）
   * 下载发起成功后退出多选，由 onStarted 收尾
   */
  const photoDownload = usePhotoDownload({
    groupID,
    folderID,
    photos,
    onStarted: exitPhotoSelect
  })

  /**
   * 文件夹下载控制器 · 当前文件夹 + 子文件夹批量下载共用（与列表页同一 hook）
   * 子文件夹批量下载发起后退出多选；单文件夹下载 isBatch=false 不受影响
   */
  const folderDownload = useFolderDownload({
    groupID,
    userID: userId,
    onStarted: (_folderIDs, isBatch) => {
      if (isBatch) {
        setSubFolderSelectMode(false)
        setSelectedSubFolderIds(new Set())
      }
    }
  })

  // ── 子文件夹多选 handlers（对齐列表页 grid 管理；无框选/拖动/右键）──────────

  /** 退出子文件夹多选 + 清空选中 */
  const exitSubFolderSelect = useCallback(() => {
    setSubFolderSelectMode(false)
    setSelectedSubFolderIds(new Set())
  }, [])

  /**
   * 勾选框点击 / 多选态整卡点击 · 统一入口
   *  · 非多选态：进入多选并选中本卡
   *  · 多选态：toggle 本卡；反勾到空集合由下方 effect 自动退出多选
   * 与照片多选互斥：进入子文件夹多选时退出照片多选
   */
  const handleSubFolderSelectClick = useCallback(
    (id: string) => {
      exitPhotoSelect()
      setSelectedSubFolderIds((prev) => {
        const next = new Set(prev)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        if (next.size > 0) setSubFolderSelectMode(true)
        return next
      })
    },
    [exitPhotoSelect]
  )

  /** 全选 / 取消全选 · 已加载的全部子文件夹 */
  const handleToggleSelectAllSubFolders = useCallback(() => {
    setSelectedSubFolderIds((prev) => {
      const allSelected =
        subFolders.length > 0 && prev.size === subFolders.length
      return allSelected
        ? new Set<string>()
        : new Set(subFolders.map((f) => f.folderId))
    })
  }, [subFolders])

  /** 批量下载选中子文件夹 · 复用 folderDownload（isBatch=true） */
  const handleBatchDownloadSubFolders = useCallback(() => {
    const ids = Array.from(selectedSubFolderIds)
      .map(Number)
      .filter((n) => Number.isFinite(n))
    if (ids.length === 0) return
    trackDetailClick('download', { path: folderPath })
    folderDownload.open(ids, true)
  }, [selectedSubFolderIds, folderPath, folderDownload])

  /** 批量移动选中子文件夹 · 弹 FoldersPicker（resource.folders） */
  const handleBatchMoveSubFolders = useCallback(() => {
    if (selectedSubFolderIds.size === 0) return
    trackDetailClick('moveTo', { path: folderPath })
    setMovingFolderIds(Array.from(selectedSubFolderIds))
  }, [selectedSubFolderIds, folderPath])

  /** 移动子文件夹完成 · 关 picker + 退出多选 + 刷新列表 */
  const handleMoveSubFoldersSuccess = useCallback(() => {
    setMovingFolderIds(null)
    exitSubFolderSelect()
    loadSubFolders()
  }, [exitSubFolderSelect, loadSubFolders])

  /** 批量删除选中子文件夹 · 二次确认 → batchDeleteFolders → 退出多选 + 刷新 */
  const handleBatchDeleteSubFolders = useCallback(() => {
    const ids = Array.from(selectedSubFolderIds)
      .map(Number)
      .filter((n) => Number.isFinite(n))
    if (ids.length === 0 || !groupID) return
    trackDetailClick('delete', { path: folderPath })
    Modal.confirm({
      title: `确定删除选中的 ${ids.length} 个文件夹？`,
      content:
        '文件夹删除后不可恢复，文件夹内的照片不会被删除，会保留在【全部照片】中',
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: () =>
        Promise.resolve(
          dispatch(batchDeleteFolders({ folderIDs: ids, groupID }))
        ).then((res: { status?: number; msg?: string } | undefined) => {
          if (res && res.status === 0) {
            message.success(`已删除 ${ids.length} 个文件夹`)
            exitSubFolderSelect()
            loadSubFolders()
            return
          }
          message.error((res && res.msg) || '删除失败，请稍后重试')
        })
    })
  }, [
    selectedSubFolderIds,
    groupID,
    folderPath,
    dispatch,
    exitSubFolderSelect,
    loadSubFolders
  ])

  // 选中清空 → 自动退出子文件夹多选（全选取消 / 反勾到空，对齐列表页）
  useEffect(() => {
    if (subFolderSelectMode && selectedSubFolderIds.size === 0) {
      setSubFolderSelectMode(false)
    }
  }, [subFolderSelectMode, selectedSubFolderIds])

  // 进入照片多选时清掉子文件夹多选（两套多选互斥，避免双工具条并存）
  useEffect(() => {
    if (photoSelectMode) {
      setSubFolderSelectMode(false)
      setSelectedSubFolderIds(new Set())
    }
  }, [photoSelectMode])

  /**
   * 批量下载选中照片 · 埋点后打开照片命名弹窗（命名候选拉取 / 发起下载由 hook 负责）
   * 多选工具条主按钮、右键「下载」都走这里，targetIds 来自各自上下文
   */
  const handleBatchDownloadPhotos = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) return
      trackDetailClick('download', { path: folderPath })
      photoDownload.open(ids)
    },
    [folderPath, photoDownload]
  )

  /**
   * 批量从当前文件夹移除选中照片（不删除照片本身）
   *
   * removePhotosFromFolder({ groupID, folderID, photoIDs })
   * action 内部走 exceptionHelper · 失败会 reject，所以这里只关心成功
   */
  const handleBatchRemovePhotos = useCallback(
    (ids: string[]) => {
      if (ids.length === 0 || !groupID) return
      trackDetailClick('remove', { path: folderPath })
      Modal.confirm({
        title: `确定从此文件夹移除 ${ids.length} 张照片？`,
        content:
          '照片不会被删除，仍保留在【全部照片】中；可以再次添加到任意文件夹',
        okText: '移除',
        cancelText: '取消',
        onOk: () => {
          setRemovingPhotos(true)
          return Promise.resolve(
            dispatch(
              removePhotosFromFolder({ groupID, folderID, photoIDs: ids })
            )
          )
            .then(() => {
              message.success(`已从此文件夹移除 ${ids.length} 张照片`)
              // 本地把这些照片从 state 里剔掉，避免等下一次 fetch 才更新
              setPhotos((prev) => prev.filter((p) => !ids.includes(p.id)))
              setPhotoTotal((prev) => Math.max(0, prev - ids.length))
              exitPhotoSelect()
            })
            .catch(() => {
              message.error('移除失败，请稍后重试')
            })
            .finally(() => {
              setRemovingPhotos(false)
            })
        }
      })
    },
    [groupID, folderID, dispatch, exitPhotoSelect]
  )

  // ── 3 个新增的批量动作 ────────────────────────
  // 编辑水印 · 去水印 · 移动到文件夹

  /**
   * 把 photoId 列表转 file URL 列表（缺 file 字段的项跳过）
   * 给"去水印"等需要 file URL 的批量动作使用
   * file 字段在 photo 加载时由 mapper 透传
   */
  const getPhotoFilesByIds = useCallback(
    (ids: string[]): string[] => {
      if (ids.length === 0) return []
      const idSet = new Set(ids)
      return photos
        .filter((p) => idSet.has(p.id) && !!p.file)
        .map((p) => p.file as string)
    },
    [photos]
  )

  /**
   * 批量编辑水印 · 把选中照片塞进 photoPicker store（from='batch-editor'），
   * 跳到 /watermark/batchEditor，由批量编辑页消费 photoPicker 数据
   *
   * 数据要求：每条至少含 seq + smallURL + file（编辑页内部会读这些字段）
   * targetIds 可选：右键单条传 [photo.id]
   */
  const handleBatchEditWatermark = useCallback(
    (targetIds?: string[]) => {
      const ids = targetIds ?? Array.from(selectedPhotoIds)
      if (ids.length === 0) return
      trackDetailClick('editWatermark', { path: folderPath })
      const idSet = new Set(ids)
      const selectedPhotos = photos.filter((p) => idSet.has(p.id))
      const usable = selectedPhotos.filter((p) => p.seq != null && p.file)
      if (usable.length === 0) {
        message.warning(
          '选中的照片缺少必要字段（seq / file），无法进入批量编辑水印'
        )
        return
      }
      if (usable.length !== selectedPhotos.length) {
        message.warning(
          `已跳过 ${
            selectedPhotos.length - usable.length
          } 张缺字段的照片，剩余 ${usable.length} 张可编辑`
        )
      }
      dispatch(setPhotoPickerPhotos(usable, 'batch-editor'))
      navigate('/watermark/batchEditor')
    },
    [selectedPhotoIds, photos, dispatch, navigate, folderPath]
  )

  /**
   * 创建去水印任务 · 调用 createProducePhotoTask 后弹 RemoveWatermarkModal
   * 与 photoFolders/Detail/HeaderExtra/BatchRemoveWatermarkButton 同款流程
   */
  const createRemoveWmTask = useCallback(
    (srcUrls: string[], total: number) => {
      if (!groupID || !userId) return
      removeWmSrcUrlsRef.current = srcUrls
      Promise.resolve(
        dispatch(
          createProducePhotoTask({
            userID: userId,
            groupID,
            srcUrls,
            type: 1,
            source: 'web_remove_watermark_batch'
          })
        )
      )
        .then((res: { taskID?: string } | null | undefined) => {
          if (!res || !res.taskID) return
          setRemoveWmModal({
            visible: true,
            taskID: res.taskID,
            fileName: '',
            photoNum: total
          })
          // 弹起任务 modal 后退多选，避免选择态卡住
          exitPhotoSelect()
        })
        .catch(SentryUtils.capturePromiseException)
    },
    [groupID, userId, dispatch, exitPhotoSelect]
  )

  /**
   * 批量去水印 · 与 BatchRemoveWatermarkButton 同款两阶段
   *  1. batchRemoveWatermarkCheck 预检：分出可去水印 urls / 不可去水印 invalidUrls
   *  2. 用户确认 → createRemoveWmTask → 弹 RemoveWatermarkModal 跟踪任务进度
   *
   * targetIds 可选：右键单条传 [photo.id]
   */
  const handleBatchRemoveWatermark = useCallback(
    async (targetIds?: string[]) => {
      const ids = targetIds ?? Array.from(selectedPhotoIds)
      if (!groupID || ids.length === 0) return
      trackDetailClick('removeWatermark', { path: folderPath })
      const include = getPhotoFilesByIds(ids)
      if (include.length === 0) {
        message.warning('选中的照片缺少原图地址，无法去水印')
        return
      }
      try {
        const res = (await Promise.resolve(
          dispatch(
            batchRemoveWatermarkCheck({
              filter: { include, exclude: [] },
              groupID
            })
          )
        )) as
          | {
              invalidUrls?: string[]
              urls?: string[]
              total?: number
              status?: number
            }
          | undefined
        if (!res) {
          message.error('请求失败，请稍后重试')
          return
        }
        const total = res.total ?? include.length
        const urls = res.urls ?? []
        const invalidUrls = res.invalidUrls ?? []
        if (res.status === -122) {
          message.warning('任务过多，请稍后重试')
          return
        }
        if (res.status === -125) {
          message.warning(`当前选中 ${total} 张照片，批量去水印最多支持 200 张`)
          return
        }
        if (urls.length === 0) {
          message.warning('所选照片均无法去水印，请重新选择')
          return
        }
        if (invalidUrls.length === 0) {
          createRemoveWmTask(urls, total)
          return
        }
        Modal.confirm({
          title: '检测到有不可去水印的照片',
          icon: 'info-circle',
          content: `本次共选择 ${total} 张照片，检测到 ${invalidUrls.length} 张不可去水印，点击继续将会跳过这 ${invalidUrls.length} 张。`,
          okText: '继续去水印',
          onOk: () => {
            createRemoveWmTask(urls, total)
          }
        })
      } catch (e) {
        SentryUtils.capturePromiseException(e)
      }
    },
    [
      groupID,
      selectedPhotoIds,
      getPhotoFilesByIds,
      dispatch,
      createRemoveWmTask,
      folderPath
    ]
  )

  /**
   * 批量移动照片 · 弹 FoldersPicker（action='move'），目标选定后由 picker 内部
   * dispatch(cutPhotos)。成功 → 本页本地剔除被移走的照片 + 退多选
   *
   * 支持参数化（targetIds）让右键菜单单条移动也能复用
   */
  const handleBatchMovePhotos = useCallback(
    (targetIds?: string[]) => {
      const ids = targetIds ?? Array.from(selectedPhotoIds)
      if (ids.length === 0) return
      trackDetailClick('moveTo', { path: folderPath })
      setMovingPhotoIds(ids)
    },
    [selectedPhotoIds, folderPath]
  )

  const handleMovePhotosSuccess = useCallback(() => {
    const ids = movingPhotoIds ?? []
    setMovingPhotoIds(null)
    if (ids.length === 0) return
    const idSet = new Set(ids)
    setPhotos((prev) => prev.filter((p) => !idSet.has(p.id)))
    setPhotoTotal((prev) => Math.max(0, prev - ids.length))
    exitPhotoSelect()
  }, [movingPhotoIds, exitPhotoSelect])

  /**
   * 批量删除照片 · 走 deletePhoto · photo_list 字段是 photo.seq（非 photo.id）
   * 二次确认 + 成功后本地剔除 + 退多选
   */
  const handleBatchDeletePhotos = useCallback(
    (targetIds?: string[]) => {
      if (!groupID) return
      const ids = targetIds ?? Array.from(selectedPhotoIds)
      if (ids.length === 0) return
      trackDetailClick('delete', { path: folderPath })
      const idSet = new Set(ids)
      const seqs = photos
        .filter((p) => idSet.has(p.id))
        .map((p) => p.seq)
        .filter((s) => s != null) as Array<string | number>
      if (seqs.length === 0) {
        message.warning('选中的照片缺少 seq 字段，无法删除')
        return
      }
      Modal.confirm({
        title: `确定删除选中的 ${seqs.length} 张照片?`,
        content: '照片删除后不可恢复，请确认操作',
        okText: '删除',
        okType: 'danger',
        cancelText: '取消',
        onOk: () => {
          setDeletingPhotos(true)
          return Promise.resolve(
            dispatch(
              deletePhoto({
                group_id: groupID,
                photo_list: seqs
              })
            )
          )
            .then((res: { status?: number } | undefined) => {
              if (!res) {
                message.error('删除失败,请稍后重试')
                return
              }
              if (res.status !== 0) {
                message.error(`删除失败(status=${res.status})`)
                return
              }
              message.success(`已删除 ${ids.length} 张照片`)
              setPhotos((prev) => prev.filter((p) => !idSet.has(p.id)))
              setPhotoTotal((prev) => Math.max(0, prev - ids.length))
              exitPhotoSelect()
            })
            .catch(SentryUtils.capturePromiseException)
            .finally(() => {
              setDeletingPhotos(false)
            })
        }
      })
    },
    [groupID, selectedPhotoIds, photos, dispatch, exitPhotoSelect]
  )

  /**
   * 右键菜单 · 单条照片 · 与多选 portal 工具条对齐功能
   *
   * 上下文目标 targetIds：
   *  · 当前选中集合非空且包含被右键的卡 → 命中批量（用整个选中集合）
   *  · 否则 → 仅针对被右键的这张
   *
   * 菜单项与 portal 工具条一一对应：
   *   下载 / 编辑水印 / 去水印 / 移动 / 移出文件夹 / 删除
   *  · 后 3 项受 isAdmin 守门
   *  · 选中 ≥ 2 张且命中右键卡时，菜单项后缀展示"(N 张)"
   */
  const buildPhotoContextMenu = useCallback(
    (photo: PhotoItem) => {
      const selected = Array.from(selectedPhotoIds)
      const isBatch = selected.length > 1 && selectedPhotoIds.has(photo.id)
      const targetIds = isBatch ? selected : [photo.id]
      const countSuffix = isBatch ? (
        <span style={{ color: '#86909c', marginLeft: 8 }}>
          ({selected.length} 张)
        </span>
      ) : null
      return (
        <Menu
          onClick={({ key, domEvent }) => {
            domEvent.stopPropagation()
            switch (key) {
              case 'download':
                handleBatchDownloadPhotos(targetIds)
                break
              case 'edit-watermark':
                handleBatchEditWatermark(targetIds)
                break
              case 'remove-watermark':
                handleBatchRemoveWatermark(targetIds)
                break
              case 'move':
                handleBatchMovePhotos(targetIds)
                break
              case 'remove':
                handleBatchRemovePhotos(targetIds)
                break
              case 'delete':
                handleBatchDeletePhotos(targetIds)
                break
              default:
                break
            }
          }}
        >
          <Menu.Item key="download">
            <DownloadOutlined /> 下载
            {countSuffix}
          </Menu.Item>
          <Menu.Item key="edit-watermark">
            <EditOutlined /> 编辑水印
            {countSuffix}
          </Menu.Item>
          <Menu.Item key="remove-watermark">
            <ScissorOutlined /> 去水印
            {countSuffix}
          </Menu.Item>
          {isAdmin && (
            <Menu.Item key="move">
              <FolderOutlined /> 移动到
              {countSuffix}
            </Menu.Item>
          )}
          {isAdmin && <Menu.Divider />}
          {isAdmin && (
            <Menu.Item key="remove">
              <ExportOutlined /> 从文件夹移除
              {countSuffix}
            </Menu.Item>
          )}
          {isAdmin && (
            <Menu.Item key="delete" style={{ color: '#ef4444' }}>
              <DeleteOutlined /> 删除
              {countSuffix}
            </Menu.Item>
          )}
        </Menu>
      )
    },
    [
      selectedPhotoIds,
      isAdmin,
      handleBatchDownloadPhotos,
      handleBatchEditWatermark,
      handleBatchRemoveWatermark,
      handleBatchMovePhotos,
      handleBatchRemovePhotos,
      handleBatchDeletePhotos
    ]
  )

  /** 全选 / 已选 N 项的派生态 */
  const allPhotoSelected = useMemo(() => {
    if (photos.length === 0) return false
    return photos.every((p) => selectedPhotoIds.has(p.id))
  }, [photos, selectedPhotoIds])

  /**
   * 进入"添加照片"页 · 复用老页面 /photo/folder/add/:id（这一版不重做）。
   * 带 from=aiClassify：让添加页的返回 / 完成跳回 AI 分类详情，而不是旧照片详情页
   * （两边用同一个 folderID，无法靠 folder 类型反推来源，必须显式带）。
   */
  const handleAddPhoto = useCallback(() => {
    trackDetailClick('addPhoto', { path: folderPath })
    navigate(`/photo/folder/add/${folderID}?from=aiClassify`)
  }, [navigate, folderID, folderPath])

  /**
   * 主动唤起下载记录抽屉 · 头部「下载记录」按钮触发（与列表页 ⋯ 菜单同款）
   * 只置 redux visible=true，抽屉内部会在 visible 变 true 时自行拉取任务列表
   */
  const handleOpenDownloadHistory = useCallback(() => {
    trackDetailClick('downloadRecord', { path: folderPath })
    dispatch(setExportListDrawerVisible(true))
  }, [dispatch, folderPath])

  // ── 当前文件夹的操作（下载 / 重命名 / 删除 / 移动）──
  // 由面包屑当前节点的 caret-down 菜单触发；与列表页右键单条菜单语义一致
  const [renameOpen, setRenameOpen] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const [renaming, setRenaming] = useState(false)
  const [moveOpen, setMoveOpen] = useState(false)

  /** 下载当前文件夹 · 埋点后打开下载命名弹窗（拉候选 / 发起下载由 folderDownload 负责） */
  const handleFolderDownload = useCallback(() => {
    trackDetailClick('downloadAsZip', { path: folderPath })
    folderDownload.open([folderID], false)
  }, [folderPath, folderID, folderDownload])

  /** 打开重命名弹窗 · 预填当前 folderName */
  const handleFolderRenameStart = useCallback(() => {
    setRenameValue(folderInfo?.folderName ?? '')
    setRenameOpen(true)
  }, [folderInfo])

  /** 确认重命名 · 与列表页 handleSingleRename 同款 trim / maxLength 32 */
  const handleFolderRenameConfirm = useCallback(() => {
    if (!groupID) return
    const next = (renameValue || '').trim()
    if (!next) {
      message.warning('文件夹名不能为空')
      return
    }
    if (next.length > 32) {
      message.warning('文件夹名最多 32 字')
      return
    }
    if (next === (folderInfo?.folderName ?? '')) {
      setRenameOpen(false)
      return
    }
    setRenaming(true)
    dispatch(
      updateFolder({
        groupID,
        folderID,
        folderName: next
      })
    )
      .then(() => {
        // updateFolder 内部已弹 message.success
        // 本地同步更新 folderInfo 让面包屑立刻刷新
        setFolderInfo((prev) => (prev ? { ...prev, folderName: next } : prev))
        setRenameOpen(false)
      })
      .finally(() => {
        setRenaming(false)
      })
  }, [dispatch, folderID, folderInfo, groupID, renameValue])

  /** 删除当前文件夹 · 二次确认 → deleteFolder → 返回上一级 */
  const handleFolderDelete = useCallback(() => {
    if (!groupID) return
    Modal.confirm({
      title: `确定删除「${folderInfo?.folderName ?? ''}」？`,
      content:
        '文件夹删除后不可恢复，文件夹内的照片不会被删除，会保留在【全部照片】中',
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: () => {
        return dispatch(
          deleteFolder({
            groupID,
            folderID
          })
        ).then((res: { status?: number; msg?: string } | undefined) => {
          if (!res || res.status === 0) {
            message.success('删除成功')
            // 删除后跳回上一级（父文件夹或根列表）
            const parents = folderInfo?.parentFolders ?? []
            const lastParent = parents[parents.length - 1]
            if (lastParent) {
              navigate(`/ai-classify/folder/${lastParent.folderID}`, {
                replace: true
              })
            } else {
              navigate('/ai-classify', { replace: true })
            }
            return
          }
          const tip = (() => {
            switch (res.status) {
              case -10:
                return '你不是管理员，无法删除文件夹'
              case -56:
              case -203:
                return '你没有删除权限，请联系团队管理员'
              default:
                return `删除失败(status=${res.status})${
                  res.msg ? ' · ' + res.msg : ''
                }`
            }
          })()
          message.error(tip)
        })
      }
    })
  }, [dispatch, folderID, folderInfo, groupID, navigate])

  /** 打开「移动到文件夹」picker · cut 成功后返上一级（避免停留在已挪走的当前 folder 路径上） */
  const handleFolderMove = useCallback(() => {
    setMoveOpen(true)
  }, [])

  const handleFolderMoveSuccess = useCallback(() => {
    setMoveOpen(false)
    // 移动后当前 folderID 的 parent 链已变 · 重新拉一次 folder info 让面包屑刷新
    setFolderLoading(true)
    Promise.resolve(dispatch(getFolderInfo({ groupID, folderID })))
      .then((data: unknown) => {
        if (data) setFolderInfo(data as BackendFolderInfo)
      })
      .finally(() => setFolderLoading(false))
  }, [dispatch, folderID, groupID])

  // ── 派生状态 ───────────────────────────────────
  const subFolderCount = subFolders.length
  const isEmpty = useMemo(
    () => photoTotal === 0 && subFolderCount === 0 && !photoLoading,
    [photoTotal, subFolderCount, photoLoading]
  )

  return (
    <ChatActionsProvider value={chatActions}>
      <Layout className={styles.page}>
        <div className={styles.body}>
          <div className={styles.mainColumn}>
            <Spin
              spinning={folderLoading}
              wrapperClassName={styles.spinWrapper}
            >
              <DetailHeader
                folderName={folderInfo?.folderName ?? ''}
                folderType={folderInfo?.folderType ?? 0}
                parentFolders={folderInfo?.parentFolders ?? []}
                photoCount={photoTotal}
                subFolderCount={subFolderCount}
                rule={rule}
                canCreateSubFolder={
                  isAdmin && (folderInfo?.folderType ?? 0) === 0
                }
                canSelectPhotos={photoTotal > 0}
                photoSelectMode={photoSelectMode}
                onBack={handleBack}
                onAddPhoto={handleAddPhoto}
                onCreateSubFolder={handleOpenCreateSub}
                onTogglePhotoSelect={handleTogglePhotoSelect}
                // parentFolders 字段类型是 number，复用 handleSubFolderClick 时转 string
                onNavigateToFolder={(id) => handleSubFolderClick(String(id))}
                aiSidebarOpen={sidebarOpen}
                onToggleAISidebar={() => dispatch(toggleSidebar())}
                onOpenDownloadHistory={handleOpenDownloadHistory}
                canManageFolder={isAdmin}
                onFolderDownload={handleFolderDownload}
                onFolderRename={handleFolderRenameStart}
                onFolderDelete={handleFolderDelete}
                onFolderMove={handleFolderMove}
                onPhotoActionsSlotRef={setPhotoActionsSlot}
              />

              <Content className={styles.content}>
                {isEmpty ? (
                  <EmptyDetailState onAddPhoto={handleAddPhoto} />
                ) : (
                  (photoTotal > 0 || subFolderCount > 0) && (
                    <PhotoGrid
                      photos={photos}
                      loading={photoLoading}
                      hasMore={!photoEnd}
                      onLoadMore={handleLoadMore}
                      selectMode={photoSelectMode}
                      selectedIds={selectedPhotoIds}
                      onToggleSelect={handleTogglePhotoOne}
                      onMarqueeSelect={handleMarqueeSelect}
                      onPreview={handlePreviewPhoto}
                      buildContextMenu={buildPhotoContextMenu}
                      // 子文件夹融合进 PhotoGrid 顶部 section · 共用列宽，紧凑卡片
                      subFolders={subFolders}
                      onSelectSubFolder={handleSubFolderClick}
                      subFolderSelectMode={subFolderSelectMode}
                      subFolderSelectedIds={selectedSubFolderIds}
                      onSubFolderSelectClick={handleSubFolderSelectClick}
                      subFolderHeader={
                        subFolderSelectMode ? (
                          <span
                            className={styles.subFolderManageBar}
                            role="toolbar"
                            aria-label="子文件夹批量操作"
                          >
                            <Checkbox
                              checked={
                                subFolders.length > 0 &&
                                selectedSubFolderIds.size === subFolders.length
                              }
                              indeterminate={
                                selectedSubFolderIds.size > 0 &&
                                selectedSubFolderIds.size < subFolders.length
                              }
                              onChange={handleToggleSelectAllSubFolders}
                            />
                            <span className={styles.subFolderSectionTitle}>
                              子文件夹
                            </span>
                            <span className={styles.subFolderManageCount}>
                              已选 <strong>{selectedSubFolderIds.size}</strong>{' '}
                              项
                            </span>
                            <Button
                              type="primary"
                              ghost
                              icon={<DownloadOutlined />}
                              disabled={
                                selectedSubFolderIds.size === 0 ||
                                folderDownload.downloading
                              }
                              loading={folderDownload.downloading}
                              onClick={handleBatchDownloadSubFolders}
                            >
                              下载
                            </Button>
                            {isAdmin && (
                              <Button
                                icon={<FolderOutlined />}
                                disabled={selectedSubFolderIds.size === 0}
                                onClick={handleBatchMoveSubFolders}
                              >
                                移动到
                              </Button>
                            )}
                            {isAdmin && (
                              <Button
                                danger
                                ghost
                                icon={<DeleteOutlined />}
                                disabled={selectedSubFolderIds.size === 0}
                                onClick={handleBatchDeleteSubFolders}
                              >
                                删除
                              </Button>
                            )}
                            <Button
                              type="link"
                              className={styles.cancelBtn}
                              onClick={exitSubFolderSelect}
                            >
                              取消
                            </Button>
                          </span>
                        ) : undefined
                      }
                    />
                  )
                )}
              </Content>
              {/* 多选底部操作条 · 移到 Spin 内 / Content 外作为 flex 兄弟，
            避免 antd Spin 的 .ant-spin-nested-loading 没拿到 flex:1
            导致 Content 内 sticky 失效；现在它作为 .spinWrapper（flex column）
            的固定高度子元素，永远在页面底部可见 */}
              {/* 照片多选批量操作 · portal 投到 DetailHeader 头部按钮区
                  原底部 sticky 操作条已下线，统一改为顶部右上角触发
                  · 全选 / 已选 N / 下载主按钮 / 编辑水印 / 去水印 / 移动
                  · "取消" 按钮独立留在 DetailHeader 头部最右（不在 portal 内），
                    与列表页文件夹多选 portal 设计一致 */}
              {photoSelectMode &&
                photoTotal > 0 &&
                photoActionsSlot &&
                createPortal(
                  <span
                    className={styles.headerPhotoActions}
                    role="toolbar"
                    aria-label="照片批量操作"
                  >
                    <Button
                      onClick={
                        allPhotoSelected
                          ? handleClearSelection
                          : handleSelectAllPhotos
                      }
                    >
                      {allPhotoSelected ? '取消全选' : '全选'}
                    </Button>
                    <span className={styles.headerPhotoActionsCount}>
                      已选 <strong>{selectedPhotoIds.size}</strong> 张
                    </span>
                    {/* 下载选中照片为压缩包 · 普通主按钮 */}
                    <Button
                      type="primary"
                      icon={<DownloadOutlined />}
                      disabled={
                        selectedPhotoIds.size === 0 || photoDownload.downloading
                      }
                      onClick={() =>
                        handleBatchDownloadPhotos(Array.from(selectedPhotoIds))
                      }
                    >
                      {photoDownload.downloading ? '下载中…' : '下载'}
                    </Button>
                    <Button
                      icon={<EditOutlined />}
                      disabled={selectedPhotoIds.size === 0}
                      onClick={() => handleBatchEditWatermark()}
                    >
                      编辑水印
                    </Button>
                    <Button
                      icon={<ScissorOutlined />}
                      disabled={selectedPhotoIds.size === 0}
                      onClick={() => handleBatchRemoveWatermark()}
                    >
                      去水印
                    </Button>
                    {/* 移动到 / 移出文件夹 / 删除 合并按钮组
                        默认点击主按钮 = 移出文件夹（非破坏 · 照片仍在全部照片中）
                        右侧 caret 弹下拉菜单 · 选 [移动到 / 移出文件夹 / 删除]
                        删除项红色警示且二次确认（已在 handleBatchDeletePhotos 内） */}
                    {isAdmin && (
                      <Dropdown.Button
                        icon={<CaretDownOutlined />}
                        disabled={
                          selectedPhotoIds.size === 0 ||
                          removingPhotos ||
                          deletingPhotos
                        }
                        onClick={() =>
                          handleBatchRemovePhotos(Array.from(selectedPhotoIds))
                        }
                        popupRender={() => (
                          <Menu
                            onClick={({ key, domEvent }) => {
                              domEvent.stopPropagation()
                              switch (key) {
                                case 'move':
                                  handleBatchMovePhotos()
                                  break
                                case 'remove':
                                  handleBatchRemovePhotos(
                                    Array.from(selectedPhotoIds)
                                  )
                                  break
                                case 'delete':
                                  handleBatchDeletePhotos()
                                  break
                                default:
                                  break
                              }
                            }}
                          >
                            <Menu.Item key="move">
                              <FolderOutlined /> 移动到
                            </Menu.Item>
                            <Menu.Item key="remove">
                              <ExportOutlined /> 移出文件夹
                            </Menu.Item>
                            <Menu.Item
                              key="delete"
                              style={{ color: '#ef4444' }}
                            >
                              <DeleteOutlined /> 删除
                            </Menu.Item>
                          </Menu>
                        )}
                      >
                        <ExportOutlined />
                        {removingPhotos
                          ? '移出中…'
                          : deletingPhotos
                            ? '删除中…'
                            : '移出'}
                      </Dropdown.Button>
                    )}
                  </span>,
                  photoActionsSlot
                )}
              {/* 新建子文件夹弹窗 · 复用 CreateFolderModal,parentFolderID = 当前 folderID
                  onCreated 自定义为"刷新子文件夹列表"，不跳新详情 */}
              <CreateFolderModal
                visible={createSubOpen}
                onClose={() => setCreateSubOpen(false)}
                parentFolderID={folderID}
                title="新建子文件夹"
                onCreated={handleSubFolderCreated}
              />

              {/* 重命名当前文件夹 · 由面包屑下拉菜单的"重命名"触发 */}
              <Modal
                open={renameOpen}
                title="重命名文件夹"
                okText="确认"
                cancelText="取消"
                confirmLoading={renaming}
                onOk={handleFolderRenameConfirm}
                onCancel={() => setRenameOpen(false)}
                destroyOnHidden
              >
                <Input
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  placeholder="请输入新的文件夹名"
                  maxLength={32}
                  onPressEnter={handleFolderRenameConfirm}
                  autoFocus
                />
              </Modal>

              {/* 移动当前文件夹 · 由面包屑下拉菜单的"移动到"触发；
                  cut 成功后重新拉 folder info 让面包屑同步新父级链 */}
              {moveOpen && isAdmin && (
                <FoldersPicker
                  visible={true}
                  action="move"
                  showAIFolders={false}
                  canSelectRoot={true}
                  checkedNum={0}
                  resource={{
                    folders: [String(folderID)],
                    photos: []
                  }}
                  curFolderID="0"
                  place="aiClassifyDetail"
                  source={5}
                  onRefresh={handleFolderMoveSuccess}
                  onClose={() => setMoveOpen(false)}
                />
              )}

              {/* 批量移动选中照片 · 由底部「移动」按钮 / 右键菜单触发；
                  curFolderID = 当前文件夹（让 picker 内部的 cutPhotos 知道源）
                  resource.photos 来源于 movingPhotoIds（区别于 selectedPhotoIds，
                  让右键单条移动也能复用同一个 picker 而不动选中集合） */}
              {movingPhotoIds && isAdmin && (
                <FoldersPicker
                  visible={true}
                  action="move"
                  showAIFolders={false}
                  canSelectRoot={false}
                  checkedNum={movingPhotoIds.length}
                  resource={{
                    folders: [],
                    photos: movingPhotoIds
                  }}
                  curFolderID={String(folderID)}
                  place="aiClassifyDetailPhotos"
                  source={6}
                  onRefresh={handleMovePhotosSuccess}
                  onClose={() => setMovingPhotoIds(null)}
                />
              )}

              {/* 批量移动选中子文件夹 · 子文件夹多选「移动」触发；
                  resource.folders 来源于 movingFolderIds，picker 内部走 cutFolders */}
              {movingFolderIds && isAdmin && (
                <FoldersPicker
                  visible={true}
                  action="move"
                  showAIFolders={false}
                  canSelectRoot={true}
                  checkedNum={0}
                  resource={{
                    folders: movingFolderIds,
                    photos: []
                  }}
                  curFolderID={String(folderID)}
                  place="aiClassifyDetailSubFolders"
                  source={5}
                  onRefresh={handleMoveSubFoldersSuccess}
                  onClose={() => setMovingFolderIds(null)}
                />
              )}

              {/* 批量去水印任务进度 modal · createProducePhotoTask 成功后弹起，
                  内部轮询 queryTaskStatus 显示进度并提供下载入口 */}
              <RemoveWatermarkModal
                visible={removeWmModal.visible}
                taskID={removeWmModal.taskID}
                fileName={removeWmModal.fileName}
                photoNum={removeWmModal.photoNum}
                isBatch={true}
                onRetry={() =>
                  createRemoveWmTask(
                    removeWmSrcUrlsRef.current,
                    removeWmModal.photoNum
                  )
                }
                onCancel={() =>
                  setRemoveWmModal({
                    visible: false,
                    taskID: null,
                    fileName: '',
                    photoNum: 0
                  })
                }
              />

              {/* 文件夹下载命名弹窗 · 面包屑下拉「下载」触发；状态 / 流程由 folderDownload 持有
                  · 弹窗自带 connect，VIP 状态自读 store：非 VIP 隐藏 footer 并展示付费引导 */}
              <FolderDownloadModal
                visible={folderDownload.visible}
                onOk={folderDownload.confirm}
                folderPhotoTitlesList={folderDownload.titleList}
                onClose={folderDownload.close}
                isBatchDownload={folderDownload.isBatch}
                selectedFolderIds={folderDownload.folderIDs}
              />

              {/* 照片下载命名弹窗 · 选中照片批量下载 / 右键「下载」触发；状态 / 流程由 photoDownload 持有 */}
              <PhotoDownloadModal
                visible={photoDownload.visible}
                photoTitleList={photoDownload.photoTitleList}
                folderTitleList={photoDownload.folderTitleList}
                onOk={photoDownload.confirm}
                onClose={photoDownload.close}
              />

              {/* 大图预览 ViewerV2 · 与旧"分类文件夹"详情页同款大图体验
                  · canDelete/canRectify/canFavorite 都关掉（删除 / 整改 / 收藏不接）
                  · 不传 isHideFoldersInfo → 展示"所属文件夹"，含管理员"添加到文件夹"写操作
                  · onFetchMore 直接复用 handleLoadMore（infinite scroll 也能在预览中续拉）
                  · photos 透传给 ViewerV2（refactor 已改为普通数组形态，无需 fromJS） */}
              <Viewer
                ref={viewerRef}
                photos={immutablePhotos as never}
                onFetchMore={handleLoadMore}
                canDelete={false}
                canRectify={false}
                canFavorite={false}
                fromPlace="aiClassifyDetail"
              />
            </Spin>
          </div>
          {sidebarOpen && (
            <ResizeHandle
              onMouseDown={handleMouseDown}
              isDragging={isDragging}
            />
          )}
          <AISidebar
            visible={sidebarOpen}
            onClose={() => dispatch(closeSidebar())}
            width={width}
            className={classNames({
              [styles.sidebarDragging]: isDragging
            })}
            headerActions={<AIChatHeaderActions showTasks={false} />}
          >
            <AIChat />
          </AISidebar>
        </div>
        {/* 下载记录抽屉 · 照片 / 文件夹下载发起后由 setExportListDrawerVisible(true) 弹起 */}
        <DownloadHistory />
      </Layout>
    </ChatActionsProvider>
  )
}

export default AIClassifyFolderDetail
